import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { createChargingPoint } from "../../api/ChargingPoint"; 
import { getManufacturers } from "../../api/Manufacturer"; 
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import { getAllChargingSites } from "../../api/ChargingSites";
import PointForm from "../../components/point/PointForm";
import { DEFAULT_OCPP_VERSION, requiresBasicAuthPassword } from "../../components/point/ocppPointHelpers";
import { useTranslation } from "react-i18next";

const CreatePointPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const location = useLocation();
  const { chargingSitePublicId } = location.state || {};

  const navigate = useNavigate();
  const [chargingSites, setChargingSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [vendors, setVendors] = useState([]); 

  const [chargingPoint, setChargingPoint] = useState({
/*  chargingSiteName: "", */
    chargingSitePublicId: "",
    chargingPointName: "",
    protocolType: "OCPP",
    ocppVersion: DEFAULT_OCPP_VERSION,
    basicAuthPassword: "",
    chargingPointVendor: "",
    chargingPointModel: "",
    isMultiStationChargingPoint: null,
    supportsReservation: false,
  });

  useEffect(() => {
    const fetchChargingSites = async () => {
      setLoading(true);
      try {
        const accessToken = await getAccessToken();
        const sites = await getAllChargingSites(accessToken); 
        setChargingSites(sites);
      } catch (error) {
        toast.error(t("toast.failedFetch", { entity: t("entity.sites") }));
      } finally {
        setLoading(false);
      }
    };

    fetchChargingSites();
  }, [getAccessToken]);

  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      try {
        const accessToken = await getAccessToken();
        const vendors = await getManufacturers(accessToken);
        setVendors(vendors);
      } catch (error) {
        toast.error(t("toast.failedFetch", { entity: t("common.vendor") }));
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, [getAccessToken]);

  const handleInputChange = (key, value) => {
    setHasChanges(true);
    setChargingPoint((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (chargingSitePublicId) {
      setChargingPoint((prev) => ({ ...prev, chargingSitePublicId }));
    }
  }, [chargingSitePublicId])

  const handleCreateChargingPoint = async () => {
  const { chargingPointName, chargingSitePublicId, chargingPointModel, chargingPointVendor, isMultiStationChargingPoint, ocppVersion, basicAuthPassword } = chargingPoint;

  const missing = [];
  if (!chargingPointName) missing.push("chargingPointName");
  if (!chargingSitePublicId) missing.push("chargingSitePublicId");
  if (!chargingPointModel) missing.push("chargingPointModel");
  if (!chargingPointVendor) missing.push("chargingPointVendor");
  if (isMultiStationChargingPoint === null || isMultiStationChargingPoint === undefined) missing.push("isMultiStationChargingPoint");
  if (!ocppVersion) missing.push("ocppVersion");
  if (requiresBasicAuthPassword(ocppVersion, basicAuthPassword)) {
    missing.push("basicAuthPassword");
  }

  if (missing.length > 0) {
    setInvalidFields(missing);
    toast.error(
      missing.includes("basicAuthPassword")
        ? t("point.basicAuthRequired")
        : t("common.pleaseFillRequiredFields", { fields: missing.join(", ") })
    );
    return;
  }

  setInvalidFields([]);

    const toastId = toast.loading(t("toast.creating", { entity: t("entity.chargingPoint") }));
  
    try {
      const accessToken = await getAccessToken();
      const newPoint = await createChargingPoint(chargingPoint, accessToken); 

      toast.success(t("toast.created", { entity: t("entity.chargingPoint") }), { id: toastId });

      if(chargingSitePublicId) {
        navigate(`/point/${newPoint.chargingPointPublicId}`, { state: { chargingSitePublicId: chargingSitePublicId } });
      }
      else if (chargingSitePublicId) {
        navigate(`/point/${newPoint.chargingPointPublicId}`, { state: { chargingSitePublicId: chargingSitePublicId } });
      } else {
        navigate(`/point/${newPoint.chargingPointPublicId}`);
      }
    } catch (error) {
        toast.error(error?.message || t("toast.failedCreate", { entity: t("entity.point") }), {
        id: toastId,
      });
    } 
  };

  const navigateBack = () => {
    if(chargingSitePublicId) {
      navigate(`/site/${chargingSitePublicId}`);
    } else {
      navigate("/points");
    }
  }

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage title={t("pages.chargingPoints")} loading={loading} />
          </div>

          <div className={`${loading ? "loading" : ""} page-content`}>
            <div className="flex flex-row items-center pb-4">
              <button
                onClick={() => navigateBack()}
                className="text-black flex"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 ">{t("pages.createPoint")}</h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigateBack()}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!chargingPoint.chargingPointName && !hasChanges}
                  onClick={handleCreateChargingPoint}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>

            <div className=" px-4 py-8 shadow-lg rounded-md w-full">
              
                <PointForm 
                chargingPoint={chargingPoint} 
                handleInputChange={handleInputChange} 
                chargingSites={chargingSites} 
                invalidFields={invalidFields} 
                vendors={vendors} 
                />

              <div className="flex w-full mt-4">
                {/*<PointThirdPartyRef 
                thirdPartySystemMapping={thirdPartySystemMapping}
                setThirdPartySystemMapping={setThirdPartySystemMapping}
                setHasChanges={setHasChanges}/>*/}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CreatePointPage;
